import { ArrowClockwiseIcon, CaretRightIcon, ListMagnifyingGlassIcon } from '@phosphor-icons/react';
import { useState } from 'react';

import { Button } from '@/components/common/button';
import { Chip } from '@/components/common/chip';
import { TextButton } from '@/components/common/text-button';
import { Drawer } from '@/components/feedback/drawer';
import type { OrderDetail } from '@/features/orders';
import { CLAIM_STATUS_CHIP, formatWarrantyClaimDate } from '@/features/requests';

import { DrawerSkeleton } from '../../components/drawer-skeleton';
import { unitLabel } from '../../replacements/format';
import { useOrderWarrantyClaims } from '../hooks';

import { ClaimWarrantyDrawer } from './claim-warranty-drawer';
import styles from './warranty-claims-drawer.module.scss';
import { WarrantyPreviewDrawer } from './warranty-preview-drawer';

/** AC 112445 verbatim. */
const LOAD_ERROR = "We couldn't load your warranty claims. Please try again.";
/** AC 112444 verbatim — Figma `429:277337` draws "No request found", which is copy-pasted from the
 *  replacement frame; the AC owns copy (ADR-0023). */
const EMPTY = 'No claims found.';

export type WarrantyClaimsDrawerProps = {
  order: OrderDetail;
  open: boolean;
  onClose: () => void;
};

/**
 * Every Warranty Claim raised against one order. Reached from the Actions menu once the order is
 * delivered *and* the replacement window has elapsed — Request Replacement is the affordance until
 * then (ADR-0023). Read-only: this surface never mutates Claim Status, which stays with the
 * Requests → Warranty queue.
 *
 * A plain `<ul>`, never `<DataTable>` — this is an unpaginated, unsortable, order-scoped list.
 *
 * The Raise Warranty Claim pill appears iff the backend reports at least one Device on the order
 * still eligible, and stacks the Claim Warranty intake form on top of this drawer so closing intake
 * returns here rather than to Order Details. Clicking a row stacks the Ticket Preview the same way
 * (ADR-0022 pop-back, inherited by ADR-0023).
 */
export function WarrantyClaimsDrawer({ order, open, onClose }: WarrantyClaimsDrawerProps) {
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const { data, isPending, isError, refetch } = useOrderWarrantyClaims(order.id, open);

  return (
    <>
      <Drawer open={open} onClose={onClose} title="Warranty Claims" hideFooter>
        <div className={styles.body}>
          {isPending ? <DrawerSkeleton label="Loading warranty claims" /> : null}
          {isError ? (
            <div className={styles.notice}>
              <p className={styles.noticeText}>{LOAD_ERROR}</p>
              <TextButton
                tone="brand"
                size="sm"
                startIcon={<ArrowClockwiseIcon size={16} />}
                onClick={() => void refetch()}
              >
                Try again
              </TextButton>
            </div>
          ) : null}
          {data && data.claims.length === 0 ? (
            <div className={styles.empty}>
              <ListMagnifyingGlassIcon size={24} className={styles.emptyIcon} />
              <p className={styles.emptyText}>{EMPTY}</p>
            </div>
          ) : null}
          {data && data.claims.length > 0 ? (
            <ul className={styles.list}>
              {data.claims.map((c) => (
                <li key={c.id} className={styles.item}>
                  <button type="button" className={styles.row} onClick={() => setPreviewId(c.id)}>
                    <span className={styles.text}>
                      <span className={styles.name}>
                        {unitLabel(c.itemName, c.quantity, c.unitIndex)}
                      </span>
                      {/* No `?? bundleName` fallback: BE populates `category` for bundle components
                          too as of 112445 (2026-08-04) — "no fallback to bundle name is needed" — so
                          the old fallback would now put a Bundle name in a slot the AC calls "Device
                          Category" while a real category was available. Still `?? '—'`, since BE
                          types the field nullable. */}
                      <span className={styles.meta}>
                        {c.category ?? '—'} | {formatWarrantyClaimDate(c.requestedAt)}
                      </span>
                    </span>
                    <span className={styles.trailing}>
                      <Chip intent={CLAIM_STATUS_CHIP[c.status].intent}>
                        {CLAIM_STATUS_CHIP[c.status].label}
                      </Chip>
                      <CaretRightIcon size={16} className={styles.caret} />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {/* AC 112444: the CTA appears iff at least one Device on the order is still eligible —
              the backend's verdict, from the same fetch that drives the rows (ADR-0023). Absent in
              the view-only and all-ineligible-empty branches alike. */}
          {data?.hasEligibleDevices ? (
            <div className={styles.action}>
              <Button variant="neutral" size="large" onClick={() => setIntakeOpen(true)}>
                Raise Warranty Claim
              </Button>
            </div>
          ) : null}
        </div>
      </Drawer>

      <ClaimWarrantyDrawer
        orderId={order.id}
        customerId={order.customer.id}
        open={intakeOpen}
        onClose={() => setIntakeOpen(false)}
      />

      {/* Mounted only while a row is selected, so the id-gated detail query never fires with a
          blank claimId. Closing it pops back to this listing. */}
      {previewId ? (
        <WarrantyPreviewDrawer
          orderId={order.id}
          claimId={previewId}
          open
          onClose={() => setPreviewId(null)}
        />
      ) : null}
    </>
  );
}
